import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import ProdutoAutoComplete from "../shared/ProdutoAutoComplete";
import { formatarMoeda } from "../../../../utils/solicitacoes/formatters";

const CAMPO_COMPACTO = "w-full h-7 px-1.5 text-[11px] leading-none border border-input rounded bg-card outline-none focus:border-primary focus:ring-1 focus:ring-primary/20";
const BOTAO_ICONE_COMPACTO = "h-6 w-6 shrink-0 flex items-center justify-center rounded text-[11px]";

/** Formata uma string de centavos ("6300") como "63,00" (pt-BR, com separador de milhar). */
function centavosParaTexto(centavos) {
    if (!centavos) return "";
    return (Number(centavos) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Input de valor monetário nativo (sem primereact), usado só na tabela de comparação de cotação —
 * digitação estilo "máscara de caixa": os dígitos digitados são sempre os centavos.
 */
function InputMoneyNativo({ value, onChange, onFocus, className = "" }) {
    const centavosDoValor = Math.round((Number(value) || 0) * 100);
    const [texto, setTexto] = useState(centavosParaTexto(centavosDoValor));

    useEffect(() => {
        setTexto(centavosParaTexto(centavosDoValor));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    function handleChange(e) {
        const digitos = e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
        setTexto(centavosParaTexto(digitos));
        onChange({ target: { value: digitos ? Number(digitos) / 100 : "" } });
    }

    return (
        <input
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={texto}
            onChange={handleChange}
            onFocus={onFocus}
            className={`text-right ${CAMPO_COMPACTO} ${className}`}
        />
    );
}

/**
 * Campos de Cotação (fornecedores, comparação, observação, valor final) reaproveitados em dois
 * pontos do fluxo de Compras: na Etapa 3 (EtapaCotacao.jsx, fluxo normal) e na Etapa 1
 * (NovaSolicitacaoComprasForm.jsx, quando "Necessita de cotação?" = Não) — mesma estrutura de
 * dados/validação em ambos, só muda quem preenche e quando.
 */

export function totalLinha(linha) {
    return (Number(linha.quantidade) || 0) * (Number(linha.valorUnitario) || 0);
}

export function totalFornecedor(fornecedor) {
    return fornecedor.produtosCotados.reduce((soma, l) => soma + totalLinha(l), 0);
}

export function menorPrazoFornecedor(fornecedor) {
    const prazos = fornecedor.produtosCotados.map(l => l.prazoEntrega).filter(Boolean).sort();
    return prazos[0] || null;
}

/** Valida os campos de cotação — mesmas regras de EtapaCompraCotacaoHandler.validarCamposCotacao no backend. */
export function validarCotacao({ filialEntregaId, fornecedores, valorFinalCotacao }) {
    if (!filialEntregaId) return "Selecione a filial de entrega.";
    if (fornecedores.length === 0) return "Informe pelo menos um fornecedor.";
    for (const f of fornecedores) {
        if (!f.nome.trim()) return "Informe o nome de todos os fornecedores.";
        for (const linha of f.produtosCotados) {
            if (!(Number(linha.quantidade) > 0)) return `Informe uma quantidade válida para o item do fornecedor "${f.nome}".`;
            // 0 é aceito — o fornecedor pode simplesmente não ter cotado este produto.
            if (linha.valorUnitario === "" || linha.valorUnitario == null || Number(linha.valorUnitario) < 0) {
                return `Informe um valor unitário válido (0 ou maior) para o item do fornecedor "${f.nome}".`;
            }
            if (!linha.prazoEntrega) return `Informe o prazo de entrega para o item do fornecedor "${f.nome}".`;
        }
    }
    if (!valorFinalCotacao || Number(valorFinalCotacao) <= 0) return "Informe o valor final da cotação.";
    return null;
}

function descricaoAnexoFornecedor(nomeFornecedor) {
    return `Cotação: ${nomeFornecedor?.trim() || "Fornecedor"}`;
}

/** Anexo mais recente já enviado para este fornecedor (mesma descrição usada por anexarArquivo) — null se nenhum. */
function anexoDoFornecedor(anexos, nomeFornecedor) {
    const descricao = descricaoAnexoFornecedor(nomeFornecedor);
    const encontrados = anexos.filter(a => a.descricao === descricao);
    return encontrados[encontrados.length - 1] || null;
}

export function FornecedorCotacaoEditor({ produtos, fornecedores, onChange, onRemover, onAnexar, anexos = [], onAdicionarProduto, onRemoverProduto }) {
    const [enviandoAnexoIndex, setEnviandoAnexoIndex] = useState(null);

    function atualizarNome(index, nome) {
        onChange(fornecedores.map((f, i) => i === index ? { ...f, nome } : f));
    }

    function anexarArquivo(index, evento, nomeFornecedor) {
        const arquivo = evento.target.files?.[0];
        evento.target.value = "";
        if (!arquivo || !onAnexar) return;

        setEnviandoAnexoIndex(index);
        onAnexar(arquivo, descricaoAnexoFornecedor(nomeFornecedor))
            .then(() => toast.success("Anexo da cotação enviado!"))
            .catch(() => toast.error("Erro ao enviar o anexo da cotação."))
            .finally(() => setEnviandoAnexoIndex(null));
    }

    function atualizarValorUnitario(fIndex, produtoId, valor) {
        onChange(fornecedores.map((f, i) => {
            if (i !== fIndex) return f;
            return {
                ...f,
                produtosCotados: f.produtosCotados.map(l => l.produtoId === produtoId ? { ...l, valorUnitario: valor } : l),
            };
        }));
    }

    function atualizarQuantidade(produtoId, quantidade) {
        onChange(fornecedores.map(f => ({
            ...f,
            produtosCotados: f.produtosCotados.map(l => l.produtoId === produtoId ? { ...l, quantidade } : l),
        })));
    }

    function atualizarPrazo(fIndex, iso) {
        onChange(fornecedores.map((f, i) => {
            if (i !== fIndex) return f;
            return {
                ...f,
                produtosCotados: f.produtosCotados.map(l => ({ ...l, prazoEntrega: iso })),
            };
        }));
    }

    function quantidadeDoProduto(produto) {
        const linha = fornecedores.flatMap(f => f.produtosCotados).find(l => l.produtoId === produto.id);
        return linha ? linha.quantidade : produto.quantidade;
    }

    function prazoDoFornecedor(f) {
        return f.produtosCotados[0]?.prazoEntrega;
    }

    function adicionar() {
        onChange([...fornecedores, {
            nome: "",
            produtosCotados: produtos.map(p => ({ produtoId: p.id, quantidade: p.quantidade, valorUnitario: "", prazoEntrega: "" })),
        }]);
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Button variant="outline" label="Adicionar fornecedor" iconLeft="fa fa-plus" onClick={adicionar} />
                {onAdicionarProduto && (
                    <div className="flex flex-col gap-1.5">
                        <ProdutoAutoComplete
                            value={null}
                            placeholder="Adicionar produto à cotação..."
                            onChange={produto => produto && onAdicionarProduto(produto)}
                        />
                    </div>
                )}
            </div>
            {fornecedores.length > 0 && (
                <div className="overflow-x-auto border border-border rounded-lg">
                    <table className="w-full text-xs border-collapse">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                <th className="px-2 py-1.5 font-semibold align-bottom">Produto</th>
                                <th className="px-2 py-1.5 font-semibold w-16 text-center align-bottom">Qtde</th>
                                {fornecedores.map((f, index) => {
                                    const anexoFornecedor = onAnexar ? anexoDoFornecedor(anexos, f.nome) : null;
                                    return (
                                        <th key={index} className="px-2 py-1.5 font-semibold min-w-[180px] align-top">
                                            <div className="flex flex-col gap-1 normal-case">
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="text"
                                                        placeholder="Nome do fornecedor"
                                                        value={f.nome}
                                                        onChange={e => atualizarNome(index, e.target.value)}
                                                        className={`flex-1 font-normal ${CAMPO_COMPACTO}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        title="Remover fornecedor"
                                                        onClick={() => onRemover(index)}
                                                        className={`${BOTAO_ICONE_COMPACTO} text-destructive hover:bg-destructive/10`}
                                                    >
                                                        <i className="fa fa-trash" />
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-1 font-normal">
                                                    {onAnexar && (
                                                        <label
                                                            title={anexoFornecedor ? "Anexar outro arquivo deste fornecedor" : "Anexar cotação deste fornecedor"}
                                                            className={`${BOTAO_ICONE_COMPACTO} border border-dashed border-border cursor-pointer text-muted-foreground hover:border-primary/50 hover:bg-muted ${enviandoAnexoIndex === index ? "opacity-60 pointer-events-none" : ""}`}
                                                        >
                                                            <i className={`fa ${enviandoAnexoIndex === index ? "fa-spinner fa-spin" : "fa-paperclip"}`} />
                                                            <input
                                                                type="file"
                                                                accept="image/*,.pdf"
                                                                className="hidden"
                                                                onChange={e => anexarArquivo(index, e, f.nome)}
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                                {anexoFornecedor && (
                                                    <div
                                                        title={anexoFornecedor.nomeOriginal}
                                                        className="flex items-center gap-1 text-[10px] font-medium text-success bg-success/10 rounded px-1.5 py-0.5 max-w-full"
                                                    >
                                                        <i className="fa fa-paperclip shrink-0" />
                                                        <span className="truncate">{anexoFornecedor.nomeOriginal}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {produtos.map(produto => (
                                <tr key={produto.id} className="bg-white border-t border-border">
                                    <td className="px-2 py-1.5 text-foreground">
                                        <div className="flex items-center gap-1.5">
                                            <span className="flex-1">{produto.nome}</span>
                                            {produto.novo && (
                                                <span className="shrink-0 text-[9px] uppercase tracking-wide font-semibold text-primary bg-primary/10 rounded px-1 py-0.5">Adicional</span>
                                            )}
                                            {produto.novo && onRemoverProduto && (
                                                <button
                                                    type="button"
                                                    title="Remover produto adicional"
                                                    onClick={() => onRemoverProduto(produto.id)}
                                                    className={`${BOTAO_ICONE_COMPACTO} shrink-0 text-destructive hover:bg-destructive/10`}
                                                >
                                                    <i className="fa fa-trash" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <input
                                            type="number" min="0" step="1"
                                            value={quantidadeDoProduto(produto)}
                                            onChange={e => atualizarQuantidade(produto.id, e.target.value)}
                                            className={`text-center ${CAMPO_COMPACTO}`}
                                        />
                                    </td>
                                    {fornecedores.map((f, index) => {
                                        const linha = f.produtosCotados.find(l => l.produtoId === produto.id);
                                        return (
                                            <td key={index} className="px-2 py-1.5">
                                                <InputMoneyNativo
                                                    value={linha?.valorUnitario}
                                                    onFocus={(e) => e.target.select()}
                                                    onChange={e => atualizarValorUnitario(index, produto.id, e.target.value)}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t border-border bg-muted/40 font-semibold">
                                <td className="px-2 py-1.5 text-foreground" colSpan={2}>Valor Total</td>
                                {fornecedores.map((f, index) => (
                                    <td key={index} className="px-2 py-1.5 text-foreground whitespace-nowrap">{formatarMoeda(totalFornecedor(f))}</td>
                                ))}
                            </tr>
                            <tr className="border-t border-border bg-muted/40">
                                <td className="px-2 py-1.5 text-foreground" colSpan={2}>Prazo de Entrega</td>
                                {fornecedores.map((f, index) => (
                                    <td key={index} className="px-2 py-1.5">
                                        <input
                                            type="date"
                                            value={prazoDoFornecedor(f)}
                                            onChange={e => atualizarPrazo(index, e.target.value)}
                                            className={CAMPO_COMPACTO}
                                        />
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
